import type { PropsWithChildren } from "react";
import { useMemo } from "react";
import { awsConfig } from "../config";
import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
  from,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { fetchAuthSession } from "aws-amplify/auth";
import { useAuthenticator } from "@aws-amplify/ui-react";

const httpLink = createHttpLink({
  uri: awsConfig.API.GraphQL.endpoint,
});

export const ApolloProviderWrapper = ({ children }: PropsWithChildren) => {
  const { user } = useAuthenticator((context) => [context.user]);
  const client = useMemo(() => {
    const authMiddleware = setContext(async (operation, { headers }) => {
      if (user) {
        const tokens = (await fetchAuthSession())?.tokens;
        return {
          headers: {
            ...headers,
            authorization: `${tokens?.idToken}`,
          },
        };
      }
    });

    return new ApolloClient({
      link: from([authMiddleware, httpLink]),
      cache: new InMemoryCache({
        typePolicies: {
          // Multiple documents select the argless root field getParticipantCase
          // (the lean boot query, the question-forms variant, and the chat
          // identity query). Without normalization their results REPLACE each
          // other at ROOT_QUERY.getParticipantCase, each write erasing the
          // other's fields and triggering an alternating refetch loop.
          // Normalizing Case by caseId makes those writes field-level merges
          // of one entity instead.
          Case: {
            keyFields: ["caseId"],
          },
        },
      }),
    });
  }, [user]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
